import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Camera, Calendar } from "lucide-react";
import { usePersistedState } from "@/hooks/usePersistedState";

export default function Projects() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm, clearForm] = usePersistedState("pb:form:new-project", { title: "", description: "", shoot_date: "", client_name: "", client_email: "" });

  const load = async () => {
    try { setItems((await api.get("/projects")).data); } catch {}
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    try {
      await api.post("/projects", form);
      toast.success("Project created");
      setOpen(false); setForm({ title: "", description: "", shoot_date: "", client_name: "", client_email: "" }); clearForm();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Layout>
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="text-amber-400 text-sm tracking-widest uppercase mb-2">Studio</p>
          <h1 className="font-display text-4xl">Projects</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="btn-create-project" className="bg-amber-500 text-stone-950 hover:bg-amber-400"><Plus className="w-4 h-4 mr-2"/>New</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-display">New project</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Title</Label><Input data-testid="input-project-title" value={form.title} onChange={e=>setForm({...form,title:e.target.value})} /></div>
              <div><Label>Description</Label><Textarea data-testid="input-project-desc" value={form.description} onChange={e=>setForm({...form,description:e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Shoot date</Label><Input type="date" value={form.shoot_date} onChange={e=>setForm({...form,shoot_date:e.target.value})} /></div>
                <div><Label>Client</Label><Input data-testid="input-project-client" value={form.client_name} onChange={e=>setForm({...form,client_name:e.target.value})} /></div>
              </div>
              <div><Label>Client email</Label><Input type="email" value={form.client_email} onChange={e=>setForm({...form,client_email:e.target.value})} /></div>
            </div>
            <DialogFooter>
              <Button data-testid="btn-save-project" onClick={create} className="bg-amber-500 text-stone-950 hover:bg-amber-400">Create project</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <div className="p-12 text-center rounded-md bg-stone-900/40 border border-dashed border-white/10">
          <Camera className="w-8 h-8 mx-auto mb-3 text-stone-600"/>
          <p className="text-stone-400 mb-4">No projects yet.</p>
          <Button onClick={()=>setOpen(true)} className="bg-amber-500 text-stone-950 hover:bg-amber-400">Create your first project</Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(p => (
            <Link key={p.project_id} to={`/projects/${p.project_id}`} data-testid={`project-${p.project_id}`}
              className="group p-6 rounded-md bg-stone-900/50 border border-white/5 hover:border-amber-500/30 transition-colors">
              <h3 className="font-display text-xl mb-1">{p.title}</h3>
              <p className="text-sm text-stone-400 mb-4 line-clamp-2">{p.description || "No description"}</p>
              <div className="flex items-center gap-4 text-xs text-stone-500">
                <span>{p.photo_count} photos</span>
                {p.shoot_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3"/>{p.shoot_date}</span>}
                <span className="ml-auto capitalize px-2 py-0.5 rounded bg-white/5">{p.status}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </Layout>
  );
}
